import { FunctionComponent, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { SectionContainer } from '~/client/components/common/SectionContainer';
import {
  usePcfraDraftList,
  usePcfraPeopleList,
  usePcfraPropertyDetails,
  useRoleInformation,
} from '~/client/hooks/storeHooks';
import { toPcrfaCreatePage, toPcrfaEditPage } from '~/client/utils/navigation';
import { PcfraPerson } from '~/common/types/people';

import { ResidentsContainer } from './CurrentResidents.styled';
import { buildExistingDraftsMap } from './helpers';
import { useLogActivityDialog } from './LogActivityDialog';
import { useReconfirmSuitabilityDialog } from './ReconfirmSuitabilityDialog';
import { useRecordAssessmentDialog } from './RecordAssessmentDialog';
import { ResidentCard } from './ResidentCard';
import { useRevokePcfraDialog } from './RevokePcfraDialog';
import { useUnnecessaryStatusDialog } from './UnnecessaryStatusDialog';

type Props = {
  propertyId: number;
  showPersonWithId?: number;
};

export const CurrentResidents: FunctionComponent<Props> = ({ propertyId, showPersonWithId }) => {
  const navigate = useNavigate();

  const { isPcfraManager } = useRoleInformation();
  const people = usePcfraPeopleList(propertyId);
  const pcfraDrafts = usePcfraDraftList(propertyId);
  const propertyDetails = usePcfraPropertyDetails(propertyId);

  const { showLogActivityDialog, logActivityDialog } = useLogActivityDialog();
  const { showRecordAssessmentDialog, recordAssessmentDialog } = useRecordAssessmentDialog();
  const { showUnnecessaryStatusDialog, unnecessaryStatusDialog } = useUnnecessaryStatusDialog();
  const { showRevokePcfraDialog, revokePcfraDialog } = useRevokePcfraDialog();
  const { showReconfirmSuitabilityDialog, reconfirmSuitabilityDialog } =
    useReconfirmSuitabilityDialog();

  const existingDraftsMap = useMemo(
    () => buildExistingDraftsMap(people, pcfraDrafts),
    [people, pcfraDrafts],
  );

  const onClickCreateEditPcfra = ({ personId }: PcfraPerson) => {
    const existingDraft = existingDraftsMap[personId];

    if (existingDraft) {
      void navigate(toPcrfaEditPage(propertyId, personId, existingDraft.draftId));
    } else {
      void navigate(toPcrfaCreatePage(propertyId, personId));
    }
  };

  const onClickDiscardDraft = (person: PcfraPerson) =>
    showUnnecessaryStatusDialog(person, existingDraftsMap[person.personId]?.draftId);

  return (
    <SectionContainer title="Current residents">
      <ResidentsContainer>
        {people.map((person) => (
          <ResidentCard
            key={person.personId}
            initOpen={person.personId === showPersonWithId}
            person={person}
            riskFactorsInfo={{
              floorLevel: propertyDetails?.floorLevel,
              livesAlone: people.length < 2,
            }}
            isPcfraManager={isPcfraManager}
            pcfraActionHandlers={{
              onClickRecordAssessment: () => showRecordAssessmentDialog(person),
              onClickCreateEditPcfra: () => onClickCreateEditPcfra(person),
              onClickReconfirmSuitability: () => showReconfirmSuitabilityDialog(person),
              onClickRemoveFromQueue: () => showUnnecessaryStatusDialog(person),
              onClickDiscardDraft: () => onClickDiscardDraft(person),
              onClickRevokePcfra: () => showRevokePcfraDialog(person),
            }}
            onClickLogActivity={() => showLogActivityDialog(person)}
          />
        ))}
      </ResidentsContainer>

      {logActivityDialog}
      {recordAssessmentDialog}
      {reconfirmSuitabilityDialog}
      {unnecessaryStatusDialog}
      {revokePcfraDialog}
    </SectionContainer>
  );
};
